import { useState, useCallback } from 'react';
import dataSourceService, { DataSource, CreateDataSourceRequest, UploadFilesResponse, GetFilesResponse } from '../services/dataSourceService';
import authService from '../services/authService';

class UploadUnauthorizedError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'UploadUnauthorizedError';
  }
}

/*
Task Progress:
- [x] Analyze existing data source implementation
- [x] Identify areas for improvement in file upload UI
- [x] Add drag and drop functionality to file upload modal
- [x] Implement proper progress tracking for file uploads
- [x] Improve file selection UI with better visualization
- [x] Add file size information to selected files
- [x] Add clear all functionality for selected files
- [ ] Test all data source APIs
- [ ] Verify delete functionality works correctly
- [ ] Confirm create database functionality
- [ ] Validate file upload with actual backend
*/

export const useDataSources = () => {
  const [allDataSources, setAllDataSources] = useState<DataSource[]>([]);
  const [loading, setLoading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState<number | null>(null);
  const [error, setError] = useState<string | null>(null);

  const fetchDataSources = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const sources = await dataSourceService.getDataSources();
      setAllDataSources(prev => {
        // Filter out existing database sources and add new ones
        // Keep only file sources from previous state
        const fileSources = prev.filter(source => source.id.startsWith('file-'));
        // Ensure unique IDs by prefixing database sources
        const databaseSources = sources.map(source => ({
          ...source,
          id: `db-${source.id}`
        }));
        return [...databaseSources, ...fileSources];
      });
    } catch (err: any) {
      setError(err.message || 'Failed to fetch data sources');
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchFileSources = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const response: GetFilesResponse = await dataSourceService.getFiles();
      setAllDataSources(prev => {
        // Filter out existing file sources and add new ones
        // Keep only database sources from previous state
        const databaseSources = prev.filter(source => source.id.startsWith('db-'));
        // Ensure unique IDs by prefixing file sources
        const fileSources = response.files.map(file => ({
          ...file,
          id: `file-${file.id}`,
          type: 'files'
        }));
        return [...databaseSources, ...fileSources];
      });
    } catch (err: any) {
      setError(err.message || 'Failed to fetch file sources');
    } finally {
      setLoading(false);
    }
  }, []);

  // New function to fetch both types of sources
  const fetchAllSources = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      // Fetch both types of sources in parallel
      const [sources, fileResponse] = await Promise.all([
        dataSourceService.getDataSources(),
        dataSourceService.getFiles()
      ]);
      
      // Process database sources
      const databaseSources = sources.map(source => ({
        ...source,
        id: `db-${source.id}`
      }));
      
      // Process file sources
      const fileSources = fileResponse.files.map(file => ({
        ...file,
        id: `file-${file.id}`,
        type: 'files'
      }));
      
      // Update state with both types of sources
      setAllDataSources([...databaseSources, ...fileSources]);
      
      // Return the sources for immediate use
      return {
        databaseSources,
        fileSources
      };
    } catch (err: any) {
      setError(err.message || 'Failed to fetch data sources');
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  const createDataSource = useCallback(async (data: CreateDataSourceRequest) => {
    setLoading(true);
    setError(null);
    try {
      const newDataSource = await dataSourceService.createDataSource(data);
      // Prefix the new data source to match the existing pattern
      const prefixedDataSource = {
        ...newDataSource,
        id: `db-${newDataSource.id}`
      };
      setAllDataSources(prev => [...prev, prefixedDataSource]);
      return prefixedDataSource;
    } catch (err: any) {
      setError(err.message || 'Failed to create data source');
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

    const deleteDataSource = useCallback(async (id: string) => {
        setLoading(true);
        setError(null);
        try {
            // Remove prefix for API call
            const apiId = id.startsWith('db-') ? id.substring(3) : id;
            await dataSourceService.deleteDataSource(apiId);
            setAllDataSources(prev => prev.filter(source => source.id !== id));
        } catch (err: any) {
            setError(err.message || 'Failed to delete data source');
            throw err;
        } finally {
            setLoading(false);
        }
    }, []);

    const deleteFileDataSource = useCallback(async (id: string) => {
        setLoading(true);
        setError(null);
        try {
            // Remove prefix for API call
            const apiId = id.startsWith('file-') ? id.substring(5) : id;
            await dataSourceService.deleteFileDataSource(apiId);
            setAllDataSources(prev => prev.filter(source => source.id !== id));
        } catch (err: any) {
            setError(err.message || 'Failed to delete file data source');
            throw err;
        } finally {
            setLoading(false);
        }
    }, []);

    const deleteDataSourceById = useCallback(async (id: string, isFile: boolean = false) => {
        if (isFile) {
            return deleteFileDataSource(id);
        } else {
            return deleteDataSource(id);
        }
    }, [deleteDataSource, deleteFileDataSource]);

  const uploadFiles = useCallback(async (files: File[]) => {
    setLoading(true);
    setUploadProgress(0);
    setError(null);
    try {
      // Create a custom upload function that tracks progress
      const uploadWithProgress = async (token?: string | null): Promise<UploadFilesResponse> => {
        return new Promise((resolve, reject) => {
          // Create FormData
          const formData = new FormData();
          files.forEach((file) => {
            formData.append('files', file);
          });

          // Create XMLHttpRequest for progress tracking
          const xhr = new XMLHttpRequest();
          
          // Track upload progress
          xhr.upload.onprogress = (event) => {
            if (event.lengthComputable) {
              const progress = Math.round((event.loaded / event.total) * 100);
              setUploadProgress(progress);
            }
          };
          
          // Handle response
          xhr.onload = () => {
            if (xhr.status >= 200 && xhr.status < 300) {
              try {
                const response = JSON.parse(xhr.responseText);
                resolve(response.data);
              } catch (e) {
                reject(new Error('Failed to parse response'));
              }
            } else if (xhr.status === 401) {
              reject(new UploadUnauthorizedError('Access token expired'));
            } else {
              reject(new Error(`Upload failed with status ${xhr.status}`));
            }
          };
          
          xhr.onerror = () => {
            reject(new Error('Network error occurred during upload'));
          };
          
          // Get token from authService
          // Configure and send request
          const baseUrl = process.env.REACT_APP_API_BASE_URL || 'http://localhost:3001';
          xhr.open('POST', `${baseUrl}/api/v1/data-sources/files/upload`);
          if (token) {
            xhr.setRequestHeader('Authorization', `Bearer ${token}`);
          }
          xhr.send(formData);
        });
      };

      let response: UploadFilesResponse;
      try {
        response = await uploadWithProgress(authService.getAccessToken());
      } catch (err) {
        if (!(err instanceof UploadUnauthorizedError)) throw err;
        const refreshed = await authService.refreshToken();
        const newToken = refreshed?.data?.accessToken;
        if (!refreshed?.success || !newToken) {
          authService.logout();
          window.location.href = '/?auth=login';
          throw new Error('Session expired. Please login again.');
        }
        response = await uploadWithProgress(newToken);
      }
      
      // Instead of refreshing all data sources, directly update the file sources in the UI
      // Process the response to match the existing pattern
      const newFileSources = response.files.map(file => ({
        ...file,
        id: `file-${file.id}`,
        type: 'files'
      }));
      
      setAllDataSources(prev => {
        // Filter out existing file sources and add new ones
        // Keep database sources and existing file sources from previous state
        const databaseSources = prev.filter(source => source.id.startsWith('db-'));
        const existingFileSources = prev.filter(source => source.id.startsWith('file-'));
        
        // Always show newly uploaded files at the top of the file list
        const allFileSources = [...newFileSources, ...existingFileSources];
        return [...databaseSources, ...allFileSources];
      });
      
      return response;
    } catch (err: any) {
      setUploadProgress(null);
      setError(err.message || 'Failed to upload files');
      throw err;
    } finally {
      setTimeout(() => {
        setLoading(false);
        setUploadProgress(null);
      }, 500); // Show 100% for a moment before hiding
    }
  }, []);

    // Separate data sources by type
    const databaseSources = allDataSources.filter(source => source.id.startsWith('db-'));
    const fileSources = allDataSources.filter(source => source.id.startsWith('file-'));

    return {
        allDataSources,
        databaseSources,
        fileSources,
        loading,
        uploadProgress,
        error,
        fetchDataSources,
        fetchFileSources,
        fetchAllSources,
        createDataSource,
        deleteDataSource,
        deleteDataSourceById,
        uploadFiles
    };
};

export default useDataSources;
